import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Collapse,
  ConfigProvider,
  Divider,
  Drawer,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  SearchOutlined,
  MessageOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { gotraApi } from '../services/gotraApi';
import type {
  Gotra,
  GotraSuggestion,
  GotraSurname,
  Pravara,
  PravaraGroup,
} from '../types/gotra';
import {
  getOrderUpdatesAfterMove,
  getUngroupedPravaras,
  sortByOrder,
  sortPravaraGroupsByOrder,
  sortPravarasByOrder,
  sortSurnamesByOrder,
} from '../types/gotra';

const getIsAuthenticated = () => Boolean(localStorage.getItem('token'));

const MOBILE_BREAKPOINT = 768;
const GOTRA_FONT_SIZE_DESKTOP = 21;
const GOTRA_FONT_SIZE_MOBILE = 16;
const PRAVARA_COLOR = '#c64103';

const pravaraTagStyle: React.CSSProperties = {
  color: PRAVARA_COLOR,
  borderColor: PRAVARA_COLOR,
  background: '#fff7f0',
};

const { Text } = Typography;

const useIsMobile = (breakpoint = MOBILE_BREAKPOINT) => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const update = () => setIsMobile(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, [breakpoint]);

  return isMobile;
};

const apiErrorMessage = (error: unknown, fallback: string) => {
  const messageValue = (error as { response?: { data?: { message?: string | string[] } } })
    ?.response?.data?.message;
  if (Array.isArray(messageValue)) return messageValue.join(', ');
  if (typeof messageValue === 'string') return messageValue;
  return fallback;
};

const GotraPage: React.FC = () => {
  const isMobile = useIsMobile();
  const gotraPageFontSize = isMobile ? GOTRA_FONT_SIZE_MOBILE : GOTRA_FONT_SIZE_DESKTOP;
  const [isAuthenticated, setIsAuthenticated] = useState(getIsAuthenticated);
  const [gotras, setGotras] = useState<Gotra[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showValidated, setShowValidated] = useState(true);
  const [showUnvalidated, setShowUnvalidated] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100 });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [suggestionModalOpen, setSuggestionModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'view' | 'manage'>('view');
  const [selectedGotra, setSelectedGotra] = useState<Gotra | null>(null);
  const [suggestions, setSuggestions] = useState<GotraSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [suggestionForm] = Form.useForm();
  const [editNameForm] = Form.useForm();
  const [pravaraForm] = Form.useForm();
  const [surnameForm] = Form.useForm();
  const [categoryForm] = Form.useForm();
  const [renamePravara, setRenamePravara] = useState<{ id: number; name: string } | null>(null);
  const [renameSurname, setRenameSurname] = useState<GotraSurname | null>(null);
  const [renameCategory, setRenameCategory] = useState<{
    surnameId: number;
    id: number;
    name: string;
  } | null>(null);
  const [addCategorySurnameId, setAddCategorySurnameId] = useState<number | null>(null);
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [createGroupForm] = Form.useForm<{ pravaraIds?: number[] }>();

  const fetchGotras = useCallback(async () => {
    setLoading(true);
    try {
      const response = await gotraApi.getAll();
      setGotras(response.data);
    } catch (error) {
      console.error('Error fetching gotras:', error);
      message.error(apiErrorMessage(error, 'Failed to fetch gotras'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSuggestions = useCallback(async () => {
    if (!getIsAuthenticated()) return;
    setSuggestionsLoading(true);
    try {
      const response = await gotraApi.listSuggestions();
      setSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      message.error(apiErrorMessage(error, 'Failed to fetch suggestions'));
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  useEffect(() => {
    setIsAuthenticated(getIsAuthenticated());
    fetchGotras();
    fetchSuggestions();
  }, [fetchGotras, fetchSuggestions]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchQuery, showValidated, showUnvalidated]);

  const handleSubmitSuggestion = async (values: {
    name: string;
    phone: string;
    text: string;
  }) => {
    try {
      await gotraApi.submitSuggestion({
        name: values.name.trim(),
        phone: values.phone.trim(),
        text: values.text.trim(),
      });
      message.success('Thank you! Your suggestion was submitted.');
      setSuggestionModalOpen(false);
      suggestionForm.resetFields();
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      message.error(apiErrorMessage(error, 'Failed to submit suggestion'));
    }
  };

  const refreshSelectedGotra = useCallback(async (gotraId: number) => {
    try {
      const response = await gotraApi.getById(gotraId);
      setSelectedGotra(response.data);
      setGotras((prev) => prev.map((g) => (g.id === gotraId ? response.data : g)));
    } catch (error) {
      console.error('Error refreshing gotra:', error);
      message.error(apiErrorMessage(error, 'Failed to refresh gotra'));
    }
  }, []);

  const filteredGotras = useMemo(() => {
    let result = gotras;
    if (showValidated || showUnvalidated) {
      result = result.filter((gotra) => {
        const validated = Boolean(gotra.isValidated);
        if (validated) return showValidated;
        return showUnvalidated;
      });
    } else {
      result = [];
    }

    const q = searchQuery.trim().toLowerCase();
    if (!q) return result;
    return result.filter((gotra) => {
      if (gotra.name.toLowerCase().includes(q)) return true;
      if (gotra.pravara?.some((p) => p.name.toLowerCase().includes(q))) return true;
      if (
        gotra.pravaraGroups?.some((g) =>
          g.pravaras?.some((p) => p.name.toLowerCase().includes(q)),
        )
      ) {
        return true;
      }
      if (gotra.surnames?.some((s) => s.name.toLowerCase().includes(q))) return true;
      if (
        gotra.surnames?.some((s) =>
          s.categories?.some((c) => c.name.toLowerCase().includes(q)),
        )
      ) {
        return true;
      }
      return false;
    });
  }, [gotras, searchQuery, showValidated, showUnvalidated]);

  const openViewDrawer = (gotra: Gotra) => {
    setDrawerMode('view');
    setDrawerOpen(true);
    setSelectedGotra(gotra);
  };

  const openManageDrawer = async (gotra: Gotra) => {
    if (!getIsAuthenticated()) {
      message.info('Please log in to manage gotra data.');
      return;
    }
    setDrawerMode('manage');
    setDrawerOpen(true);
    setSelectedGotra(gotra);
    editNameForm.setFieldsValue({ name: gotra.name });
    pravaraForm.resetFields();
    surnameForm.resetFields();
    categoryForm.resetFields();
    setRenamePravara(null);
    setRenameSurname(null);
    setRenameCategory(null);
    setAddCategorySurnameId(null);
    try {
      const response = await gotraApi.getById(gotra.id);
      setSelectedGotra(response.data);
      editNameForm.setFieldsValue({ name: response.data.name });
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to load gotra details'));
    }
  };

  const handleCreate = async (values: {
    name: string;
    pravara?: string[];
    surnames?: Array<{ name: string; categories?: Array<{ name: string }> }>;
  }) => {
    try {
      const payload = {
        name: values.name.trim(),
        pravara: (values.pravara || []).map((p) => p.trim()).filter(Boolean),
        surnames: (values.surnames || [])
          .map((s) => ({
            name: s.name?.trim(),
            categories: (s.categories || [])
              .map((c) => ({ name: c.name?.trim() }))
              .filter((c) => c.name),
          }))
          .filter((s) => s.name),
      };
      await gotraApi.create(payload);
      message.success('Gotra created successfully');
      setCreateModalOpen(false);
      createForm.resetFields();
      fetchGotras();
    } catch (error) {
      console.error('Error creating gotra:', error);
      message.error(apiErrorMessage(error, 'Failed to create gotra'));
    }
  };

  const handleDeleteGotra = async (id: number) => {
    try {
      await gotraApi.delete(id);
      message.success('Gotra deleted successfully');
      if (selectedGotra?.id === id) {
        setDrawerOpen(false);
        setSelectedGotra(null);
      }
      fetchGotras();
    } catch (error) {
      console.error('Error deleting gotra:', error);
      message.error(apiErrorMessage(error, 'Failed to delete gotra'));
    }
  };

  const applyGotraUpdate = (updated: Gotra) => {
    setGotras((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
    setSelectedGotra((prev) => (prev?.id === updated.id ? updated : prev));
  };

  const syncGlobalPravaraValidation = (pravaraId: number, isValidated: boolean) => {
    const patchGotra = (gotra: Gotra): Gotra => ({
      ...gotra,
      pravara: gotra.pravara?.map((p) => (p.id === pravaraId ? { ...p, isValidated } : p)),
      pravaraGroups: gotra.pravaraGroups?.map((group) => ({
        ...group,
        pravaras: group.pravaras?.map((p) => (p.id === pravaraId ? { ...p, isValidated } : p)),
      })),
    });
    setGotras((prev) => prev.map(patchGotra));
    setSelectedGotra((prev) => (prev ? patchGotra(prev) : null));
  };

  const handleValidateGotra = async (id: number) => {
    try {
      const response = await gotraApi.validate(id);
      applyGotraUpdate(response.data);
      message.success('Gotra marked as validated');
    } catch (error) {
      console.error('Error validating gotra:', error);
      message.error(apiErrorMessage(error, 'Failed to validate gotra'));
    }
  };

  const handleUnvalidateGotra = async (id: number) => {
    try {
      const response = await gotraApi.unvalidate(id);
      applyGotraUpdate(response.data);
      message.success('Gotra marked as not validated');
    } catch (error) {
      console.error('Error unvalidating gotra:', error);
      message.error(apiErrorMessage(error, 'Failed to unvalidate gotra'));
    }
  };

  const handleValidatePravara = async (pravaraId: number) => {
    if (!selectedGotra) return;
    try {
      const response = await gotraApi.validatePravara(selectedGotra.id, pravaraId);
      applyGotraUpdate(response.data);
      const pravara = response.data.pravara?.find((p) => p.id === pravaraId);
      if (pravara) syncGlobalPravaraValidation(pravaraId, Boolean(pravara.isValidated));
      message.success('Pravara marked as validated');
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to validate pravara'));
    }
  };

  const handleUnvalidatePravara = async (pravaraId: number) => {
    if (!selectedGotra) return;
    try {
      const response = await gotraApi.unvalidatePravara(selectedGotra.id, pravaraId);
      applyGotraUpdate(response.data);
      const pravara = response.data.pravara?.find((p) => p.id === pravaraId);
      if (pravara) syncGlobalPravaraValidation(pravaraId, Boolean(pravara.isValidated));
      message.success('Pravara marked as not validated');
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to unvalidate pravara'));
    }
  };

  const handleValidatePravaraGroup = async (groupId: number) => {
    if (!selectedGotra) return;
    try {
      const response = await gotraApi.validatePravaraGroup(selectedGotra.id, groupId);
      applyGotraUpdate(response.data);
      message.success('Pravara group marked as validated');
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to validate pravara group'));
    }
  };

  const handleUnvalidatePravaraGroup = async (groupId: number) => {
    if (!selectedGotra) return;
    try {
      const response = await gotraApi.unvalidatePravaraGroup(selectedGotra.id, groupId);
      applyGotraUpdate(response.data);
      message.success('Pravara group marked as not validated');
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to unvalidate pravara group'));
    }
  };

  const handleValidateSurname = async (surnameId: number) => {
    if (!selectedGotra) return;
    try {
      const response = await gotraApi.validateSurname(selectedGotra.id, surnameId);
      applyGotraUpdate(response.data);
      message.success('Surname marked as validated');
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to validate surname'));
    }
  };

  const handleUnvalidateSurname = async (surnameId: number) => {
    if (!selectedGotra) return;
    try {
      const response = await gotraApi.unvalidateSurname(selectedGotra.id, surnameId);
      applyGotraUpdate(response.data);
      message.success('Surname marked as not validated');
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to unvalidate surname'));
    }
  };

  const handleValidateCategory = async (surnameId: number, categoryId: number) => {
    if (!selectedGotra) return;
    try {
      const response = await gotraApi.validateCategory(selectedGotra.id, surnameId, categoryId);
      applyGotraUpdate(response.data);
      message.success('Category marked as validated');
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to validate category'));
    }
  };

  const handleUnvalidateCategory = async (surnameId: number, categoryId: number) => {
    if (!selectedGotra) return;
    try {
      const response = await gotraApi.unvalidateCategory(selectedGotra.id, surnameId, categoryId);
      applyGotraUpdate(response.data);
      message.success('Category marked as not validated');
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to unvalidate category'));
    }
  };

  const renderEntityValidationTag = (isValidated?: boolean) =>
    isValidated ? (
      <Tag color="green" style={{ margin: 0 }}>
        Validated
      </Tag>
    ) : (
      <Tag color="orange" style={{ margin: 0 }}>
        Not validated
      </Tag>
    );

  const renderValidationTag = (gotra: Gotra) => renderEntityValidationTag(gotra.isValidated);

  const renderValidationControls = (
    isValidated: boolean | undefined,
    onValidate: () => void,
    onUnvalidate: () => void,
    unvalidateTitle?: string,
  ) => (
    <Space size={4} wrap onClick={(e) => e.stopPropagation()}>
      {renderEntityValidationTag(isValidated)}
      {isValidated ? (
        <Popconfirm
          title={unvalidateTitle || 'Mark as not validated?'}
          onConfirm={onUnvalidate}
          okText="Unvalidate"
          cancelText="Cancel"
        >
          <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}>
            Unvalidate
          </Button>
        </Popconfirm>
      ) : (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: 'auto' }}
          onClick={onValidate}
        >
          Validate
        </Button>
      )}
    </Space>
  );

  const handleUpdateGotraName = async (values: { name: string }) => {
    if (!selectedGotra) return;
    try {
      await gotraApi.update(selectedGotra.id, { name: values.name.trim() });
      message.success('Gotra name updated');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to update gotra name'));
    }
  };

  const handleAddPravara = async (values: { name: string }) => {
    if (!selectedGotra) return;
    try {
      await gotraApi.addPravara(selectedGotra.id, { name: values.name.trim() });
      pravaraForm.resetFields();
      message.success('Pravara added');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to add pravara'));
    }
  };

  const handleRenamePravara = async () => {
    if (!selectedGotra || !renamePravara) return;
    try {
      await gotraApi.updatePravara(selectedGotra.id, renamePravara.id, {
        name: renamePravara.name.trim(),
      });
      setRenamePravara(null);
      message.success('Pravara renamed');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to rename pravara'));
    }
  };

  const handleMovePravara = async (pravaraId: number, direction: -1 | 1) => {
    if (!selectedGotra) return;

    const updates = getOrderUpdatesAfterMove(selectedGotra.pravara, pravaraId, direction);
    if (!updates?.length) return;

    try {
      for (const { id, order } of updates) {
        await gotraApi.updatePravara(selectedGotra.id, id, { order });
      }
      message.success('प्रवर order updated');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to update pravara order'));
    }
  };

  const handleUnlinkPravara = async (pravaraId: number) => {
    if (!selectedGotra) return;
    try {
      await gotraApi.unlinkPravara(selectedGotra.id, pravaraId);
      message.success('Pravara unlinked');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to unlink pravara'));
    }
  };

  const mergeCreatedPravaraGroup = (gotra: Gotra, newGroup: PravaraGroup): Gotra => {
    const alreadyListed = (gotra.pravaraGroups || []).some((g) => g.id === newGroup.id);
    const pravaraGroups = alreadyListed
      ? gotra.pravaraGroups || []
      : sortPravaraGroupsByOrder([...(gotra.pravaraGroups || []), newGroup]);
    const memberIds = new Set(newGroup.pravaras.map((p) => p.id));
    const pravara = gotra.pravara.map((p) =>
      memberIds.has(p.id) ? { ...p, groupId: newGroup.id } : p,
    );
    return { ...gotra, pravaraGroups, pravara };
  };

  const handleCreatePravaraGroup = async (pravaraIds: number[] = []) => {
    if (!selectedGotra) return;
    const groups = selectedGotra.pravaraGroups || [];
    const order = groups.length ? Math.max(...groups.map((g) => g.order ?? 0)) + 1 : 0;
    const payload: { order: number; pravaraIds?: number[] } = { order };
    if (pravaraIds.length > 0) payload.pravaraIds = pravaraIds;

    setCreatingGroup(true);
    try {
      const response = await gotraApi.createPravaraGroup(selectedGotra.id, payload);
      const updated = mergeCreatedPravaraGroup(selectedGotra, response.data);
      setSelectedGotra(updated);
      setGotras((prev) => prev.map((g) => (g.id === selectedGotra.id ? updated : g)));
      message.success('प्रवर group created');
      setCreateGroupModalOpen(false);
      createGroupForm.resetFields();
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to create pravara group'));
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleDeletePravaraGroup = async (groupId: number) => {
    if (!selectedGotra) return;
    try {
      await gotraApi.deletePravaraGroup(selectedGotra.id, groupId);
      message.success('प्रवर group deleted');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to delete pravara group'));
    }
  };

  const handleMovePravaraGroup = async (groupId: number, direction: -1 | 1) => {
    if (!selectedGotra?.pravaraGroups?.length) return;

    const updates = getOrderUpdatesAfterMove(selectedGotra.pravaraGroups, groupId, direction);
    if (!updates?.length) return;

    try {
      for (const { id, order } of updates) {
        await gotraApi.updatePravaraGroup(selectedGotra.id, id, { order });
      }
      message.success('प्रवर group order updated');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to update pravara group order'));
    }
  };

  const handleSetPravaraGroup = async (pravaraId: number, groupId: number | null) => {
    if (!selectedGotra) return;
    try {
      await gotraApi.updatePravara(selectedGotra.id, pravaraId, { groupId });
      message.success(groupId ? 'Pravara added to group' : 'Pravara ungrouped');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to update pravara group'));
    }
  };

  const handleAddSurname = async (values: {
    name: string;
    categories?: Array<{ name: string }>;
  }) => {
    if (!selectedGotra) return;
    try {
      await gotraApi.addSurname(selectedGotra.id, {
        name: values.name.trim(),
        categories: (values.categories || [])
          .map((c) => ({ name: c.name?.trim() }))
          .filter((c) => c.name),
      });
      surnameForm.resetFields();
      message.success('Surname added');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to add surname'));
    }
  };

  const handleRenameSurname = async () => {
    if (!selectedGotra || !renameSurname) return;
    try {
      await gotraApi.updateSurname(selectedGotra.id, renameSurname.id, {
        name: renameSurname.name.trim(),
      });
      setRenameSurname(null);
      message.success('Surname renamed');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to rename surname'));
    }
  };

  const handleMoveSurname = async (surnameId: number, direction: -1 | 1) => {
    if (!selectedGotra) return;

    const updates = getOrderUpdatesAfterMove(selectedGotra.surnames, surnameId, direction);
    if (!updates?.length) return;

    try {
      for (const { id, order } of updates) {
        await gotraApi.updateSurname(selectedGotra.id, id, { order });
      }
      message.success('थर order updated');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to update surname order'));
    }
  };

  const handleDeleteSurname = async (surnameId: number) => {
    if (!selectedGotra) return;
    try {
      await gotraApi.deleteSurname(selectedGotra.id, surnameId);
      message.success('Surname deleted');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to delete surname'));
    }
  };

  const handleAddCategory = async (values: { name: string }) => {
    if (!selectedGotra || addCategorySurnameId == null) return;
    try {
      await gotraApi.addCategory(
        selectedGotra.id,
        addCategorySurnameId,
        values.name.trim(),
      );
      categoryForm.resetFields();
      setAddCategorySurnameId(null);
      message.success('Category added');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to add category'));
    }
  };

  const handleRenameCategory = async () => {
    if (!selectedGotra || !renameCategory) return;
    try {
      await gotraApi.updateCategory(
        selectedGotra.id,
        renameCategory.surnameId,
        renameCategory.id,
        renameCategory.name.trim(),
      );
      setRenameCategory(null);
      message.success('Category renamed');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to rename category'));
    }
  };

  const handleDeleteCategory = async (surnameId: number, categoryId: number) => {
    if (!selectedGotra) return;
    try {
      await gotraApi.deleteCategory(selectedGotra.id, surnameId, categoryId);
      message.success('Category deleted');
      await refreshSelectedGotra(selectedGotra.id);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to delete category'));
    }
  };

  const renderGotraPravaraTags = (gotra: Gotra) => {
    if (!gotra.pravara?.length && !gotra.pravaraGroups?.length) {
      return <Text type="secondary">—</Text>;
    }

    if (gotra.pravaraGroups?.length) {
      return (
        <Space size={[4, 4]} wrap>
          {sortPravaraGroupsByOrder(gotra.pravaraGroups).map((group) => (
            <Tag key={`group-${group.id}`} style={pravaraTagStyle}>
              {sortByOrder(group.pravaras)
                .map((p) => p.name)
                .join(' · ')}
            </Tag>
          ))}
          {getUngroupedPravaras(gotra.pravara || []).map((p) => (
            <Tag key={p.id} style={pravaraTagStyle}>
              {p.name}
            </Tag>
          ))}
        </Space>
      );
    }

    return (
      <Space size={[4, 4]} wrap>
        {sortPravarasByOrder(gotra.pravara).map((p) => (
          <Tag key={p.id} style={pravaraTagStyle}>
            {p.name}
          </Tag>
        ))}
      </Space>
    );
  };

  const renderPravaraManageActions = (
    pravara: Pravara,
    options?: { showUngroup?: boolean; showGroupSelect?: boolean },
  ) => {
    if (!selectedGotra) return [];

    const allPravaras = sortPravarasByOrder(selectedGotra.pravara || []);
    const globalIndex = allPravaras.findIndex((p) => p.id === pravara.id);
    const groups = sortPravaraGroupsByOrder(selectedGotra.pravaraGroups || []);

    const actions = [
      ...[
        renderValidationControls(
          pravara.isValidated,
          () => handleValidatePravara(pravara.id),
          () => handleUnvalidatePravara(pravara.id),
          'Mark this pravara as not validated? It is shared across gotras.',
        ),
      ],
      <Button
        key="up"
        type="text"
        size="small"
        icon={<ArrowUpOutlined />}
        disabled={globalIndex <= 0}
        onClick={() => handleMovePravara(pravara.id, -1)}
      />,
      <Button
        key="down"
        type="text"
        size="small"
        icon={<ArrowDownOutlined />}
        disabled={globalIndex < 0 || globalIndex >= allPravaras.length - 1}
        onClick={() => handleMovePravara(pravara.id, 1)}
      />,
      <Button
        key="edit"
        type="text"
        size="small"
        icon={<EditOutlined />}
        onClick={() => setRenamePravara({ id: pravara.id, name: pravara.name })}
      />,
    ];

    if (options?.showGroupSelect && groups.length > 0) {
      actions.push(
        <Select
          key="group"
          size="small"
          placeholder="Group"
          style={{ minWidth: 96 }}
          options={groups.map((group, index) => ({
            value: group.id,
            label: `Group ${index + 1}`,
          }))}
          onChange={(groupId) => handleSetPravaraGroup(pravara.id, groupId)}
        />,
      );
    }

    if (options?.showUngroup) {
      actions.push(
        <Button key="ungroup" type="link" size="small" onClick={() => handleSetPravaraGroup(pravara.id, null)}>
          Ungroup
        </Button>,
      );
    }

    actions.push(
      <Popconfirm
        key="delete"
        title="Unlink this pravara from this gotra?"
        onConfirm={() => handleUnlinkPravara(pravara.id)}
      >
        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
      </Popconfirm>,
    );

    return actions;
  };

  const renderPravaraManageLabel = (pravara: Pravara) =>
    renamePravara?.id === pravara.id ? (
      <Input
        size="small"
        value={renamePravara.name}
        onChange={(e) => setRenamePravara({ ...renamePravara, name: e.target.value })}
        onPressEnter={handleRenamePravara}
        addonAfter={
          <Button type="link" size="small" onClick={handleRenamePravara}>
            Save
          </Button>
        }
      />
    ) : (
      <Space size={8}>
        {renderEntityValidationTag(pravara.isValidated)}
        <span>{pravara.name}</span>
      </Space>
    );

  const columns = useMemo(() => {
    const base = [
      {
        title: 'गोत्र',
        dataIndex: 'name',
        key: 'name',
        render: (name: string) => <Text strong>{name}</Text>,
      },
      {
        title: 'प्रवर',
        key: 'pravara',
        render: (_: unknown, record: Gotra) => renderGotraPravaraTags(record),
      },
      {
        title: 'थर',
        key: 'surnames',
        render: (_: unknown, record: Gotra) => {
          const count = record.surnames?.length ?? 0;
          if (!count) return <Text type="secondary">—</Text>;
          return (
            <Space size={[4, 4]} wrap>
              {sortSurnamesByOrder(record.surnames)
                .slice(0, 4)
                .map((s) => (
                  <Tag key={s.id}>{s.name}</Tag>
                ))}
              {count > 4 && <Tag>+{count - 4} more</Tag>}
            </Space>
          );
        },
      },
      {
        title: 'Status',
        key: 'status',
        width: 120,
        render: (_: unknown, record: Gotra) => renderValidationTag(record),
      },
    ];

    if (!isAuthenticated) {
      return [
        ...base,
        {
          title: '',
          key: 'actions',
          width: 90,
          render: (_: unknown, record: Gotra) => (
            <Button size="small" onClick={() => openViewDrawer(record)}>
              हेर्नुहोस्
            </Button>
          ),
        },
      ];
    }

    return [
      ...base,
      {
        title: 'Actions',
        key: 'actions',
        width: 220,
        render: (_: unknown, record: Gotra) => (
          <Space>
            <Button size="small" onClick={() => openManageDrawer(record)}>
              Manage
            </Button>
            {record.isValidated ? (
              <Popconfirm
                title="Mark this gotra as not validated?"
                onConfirm={() => handleUnvalidateGotra(record.id)}
                okText="Unvalidate"
                cancelText="Cancel"
              >
                <Button size="small">Unvalidate</Button>
              </Popconfirm>
            ) : (
              <Button size="small" type="primary" onClick={() => handleValidateGotra(record.id)}>
                Validate
              </Button>
            )}
            <Popconfirm
              title="Delete this gotra and all its surnames?"
              onConfirm={() => handleDeleteGotra(record.id)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ];
  }, [isAuthenticated]);

  const suggestionColumns = [
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (value: string) => new Date(value).toLocaleString(),
    },
    { title: 'Name', dataIndex: 'name', key: 'name', width: 140 },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', width: 120 },
    { title: 'Suggestion', dataIndex: 'text', key: 'text' },
  ];

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: filteredGotras.length,
    showSizeChanger: !isMobile,
    pageSizeOptions: ['10', '20', '50', '100'],
    showTotal: isMobile ? undefined : (total: number) => `${total} gotras`,
    simple: isMobile,
    onChange: (page: number, pageSize?: number) => {
      setPagination({ current: page, pageSize: pageSize || pagination.pageSize });
    },
  };

  const renderMobileGotraItem = (gotra: Gotra) => (
    <div
      role="button"
      tabIndex={0}
      onClick={() => openViewDrawer(gotra)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openViewDrawer(gotra);
        }
      }}
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: gotra.pravara?.length || gotra.surnames?.length ? 8 : 0,
        }}
      >
        <Text strong style={{ fontSize: gotraPageFontSize * 1.1 }}>
          {gotra.name}
        </Text>
        {renderValidationTag(gotra)}
        {isAuthenticated && (
          <Space size={4} onClick={(e) => e.stopPropagation()}>
            <Button size="small" onClick={() => openManageDrawer(gotra)}>
              Manage
            </Button>
            {gotra.isValidated ? (
              <Popconfirm
                title="Mark this gotra as not validated?"
                onConfirm={() => handleUnvalidateGotra(gotra.id)}
                okText="Unvalidate"
                cancelText="Cancel"
              >
                <Button size="small">Unvalidate</Button>
              </Popconfirm>
            ) : (
              <Button size="small" type="primary" onClick={() => handleValidateGotra(gotra.id)}>
                Validate
              </Button>
            )}
            <Popconfirm
              title="Delete this gotra and all its surnames?"
              onConfirm={() => handleDeleteGotra(gotra.id)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )}
      </div>
      {gotra.pravara?.length || gotra.pravaraGroups?.length ? (
        <div style={{ marginBottom: 6 }}>{renderGotraPravaraTags(gotra)}</div>
      ) : null}
      {gotra.surnames?.length ? (
        <Space size={[4, 4]} wrap>
          {sortSurnamesByOrder(gotra.surnames)
            .slice(0, 8)
            .map((s) => (
              <Tag key={s.id} style={{ margin: 0 }}>
                {s.name}
              </Tag>
            ))}
          {gotra.surnames.length > 8 && (
            <Tag style={{ margin: 0 }}>+{gotra.surnames.length - 8}</Tag>
          )}
        </Space>
      ) : null}
    </div>
  );

  const renderReadOnlyGotraDetail = (gotra: Gotra) => (
    <div>
      <div style={{ marginBottom: 16 }}>{renderValidationTag(gotra)}</div>
      {gotra.pravara?.length || gotra.pravaraGroups?.length ? (
        <div style={{ marginBottom: 24 }}>
          <Text type="secondary">प्रवर</Text>
          <div style={{ marginTop: 6, lineHeight: 1.6, color: PRAVARA_COLOR }}>
            {gotra.pravaraGroups?.length ? (
              <>
                {sortPravaraGroupsByOrder(gotra.pravaraGroups).map((group) => (
                  <div key={group.id} style={{ marginBottom: 8 }}>
                    <Space size={8} wrap style={{ marginBottom: 4 }}>
                      {renderEntityValidationTag(group.isValidated)}
                      <Text type="secondary">Group</Text>
                    </Space>
                    <div>
                      {sortByOrder(group.pravaras)
                        .map((p) => p.name)
                        .join(' · ')}
                    </div>
                  </div>
                ))}
                {getUngroupedPravaras(gotra.pravara || []).map((p) => (
                  <div key={p.id}>
                    <Space size={8}>
                      {renderEntityValidationTag(p.isValidated)}
                      <span>{p.name}</span>
                    </Space>
                  </div>
                ))}
              </>
            ) : (
              sortPravarasByOrder(gotra.pravara)
                .map((p) => (
                  <div key={p.id}>
                    <Space size={8}>
                      {renderEntityValidationTag(p.isValidated)}
                      <span>{p.name}</span>
                    </Space>
                  </div>
                ))
            )}
          </div>
        </div>
      ) : null}
      {gotra.surnames?.length ? (
        <div>
          <Text type="secondary">थर</Text>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sortSurnamesByOrder(gotra.surnames).map((surname) => (
              <div key={surname.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                {renderEntityValidationTag(surname.isValidated)}
                <Text strong style={{ flexShrink: 0 }}>
                  {surname.name}
                </Text>
                {surname.categories?.map((cat) => (
                  <Space key={cat.id} size={4}>
                    {renderEntityValidationTag(cat.isValidated)}
                    <Tag color="orange" style={{ margin: 0 }}>
                      {cat.name}
                    </Tag>
                  </Space>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : (
        !gotra.pravara?.length && <Text type="secondary">No additional details</Text>
      )}
    </div>
  );

  return (
    <ConfigProvider theme={{ token: { fontSize: gotraPageFontSize } }}>
    <div style={{ padding: isMobile ? 12 : 24, maxWidth: '100%', overflowX: 'hidden' }}>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <h2 style={{ margin: 0, fontSize: gotraPageFontSize * 1.35 }}>गोत्र</h2>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            width: isMobile ? '100%' : 'auto',
          }}
        >
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="गोत्र, प्रावर, थर..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: isMobile ? '100%' : 280 }}
          />
          <Space wrap>
            <Checkbox
              checked={showValidated}
              onChange={(e) => setShowValidated(e.target.checked)}
            >
              Validated
            </Checkbox>
            <Checkbox
              checked={showUnvalidated}
              onChange={(e) => setShowUnvalidated(e.target.checked)}
            >
              Not validated
            </Checkbox>
          </Space>
          <Space wrap style={{ width: isMobile ? '100%' : 'auto' }}>
            <Button
              block={isMobile}
              icon={<MessageOutlined />}
              onClick={() => {
                suggestionForm.resetFields();
                setSuggestionModalOpen(true);
              }}
            >
              प्रतिक्रिया दिनुहोस्
            </Button>
            {isAuthenticated ? (
              <Button
                block={isMobile}
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  createForm.resetFields();
                  setCreateModalOpen(true);
                }}
              >
                Add Gotra
              </Button>
            ) : (
              <Link to="/login" style={{ width: isMobile ? '100%' : 'auto' }}>
                <Button block={isMobile} type="link" style={{ padding: isMobile ? undefined : 0 }}>
                  Log in to manage gotra data
                </Button>
              </Link>
            )}
          </Space>
        </div>
      </div>

      {isMobile ? (
        <List
          loading={loading}
          dataSource={filteredGotras}
          rowKey="id"
          pagination={paginationConfig}
          renderItem={renderMobileGotraItem}
          style={{ marginTop: 4 }}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredGotras}
          rowKey="id"
          loading={loading}
          pagination={paginationConfig}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '4px 0' }}>
                {record.surnames?.length ? (
                  <List
                    size="small"
                    dataSource={sortSurnamesByOrder(record.surnames)}
                    renderItem={(surname) => (
                      <List.Item>
                        <Space wrap>
                          <Text strong>{surname.name}</Text>
                          {surname.categories?.map((cat) => (
                            <Tag key={cat.id} color="orange">
                              {cat.name}
                            </Tag>
                          ))}
                          {!surname.categories?.length && (
                            <Text type="secondary" style={{ fontSize: 18 }}>
                              No categories
                            </Text>
                          )}
                        </Space>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Text type="secondary">No surnames</Text>
                )}
              </div>
            ),
            rowExpandable: (record) => (record.surnames?.length ?? 0) > 0,
          }}
        />
      )}

      {isAuthenticated && (
        <Card title="Submitted suggestions" style={{ marginTop: 24 }} size="small">
          <Table
            columns={suggestionColumns}
            dataSource={suggestions}
            rowKey="id"
            loading={suggestionsLoading}
            pagination={{ pageSize: 10, simple: isMobile }}
            size="small"
            scroll={isMobile ? { x: 640 } : undefined}
          />
        </Card>
      )}

      <Modal
        title="प्रतिक्रिया दिनुहोस्"
        open={suggestionModalOpen}
        onCancel={() => setSuggestionModalOpen(false)}
        footer={null}
        destroyOnClose
        width={isMobile ? 'calc(100vw - 32px)' : 520}
        style={isMobile ? { top: 16, maxWidth: '100%' } : undefined}
      >
        <Form form={suggestionForm} layout="vertical" onFinish={handleSubmitSuggestion}>
          <Form.Item
            name="name"
            label="आफ्नो नाम"
            rules={[{ required: true, message: 'Enter your name' }]}
          >
            <Input placeholder="e.g. राम शर्मा" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="फोन"
            rules={[{ required: true, message: 'Enter your phone number' }]}
          >
            <Input placeholder="e.g. 9801234567" />
          </Form.Item>
          <Form.Item
            name="text"
            label="प्रतिक्रिया"
            rules={[{ required: true, message: 'Describe your suggestion' }]}
          >
            <Input.TextArea rows={4} placeholder="Describe missing or incorrect gotra data…" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
              <Button onClick={() => setSuggestionModalOpen(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add Gotra"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        footer={null}
        width={isMobile ? 'calc(100vw - 32px)' : 640}
        style={isMobile ? { top: 16, maxWidth: '100%' } : undefined}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="name"
            label="Gotra name"
            rules={[{ required: true, message: 'Enter gotra name' }]}
          >
            <Input placeholder="e.g. अत्रि" />
          </Form.Item>

          <Divider orientation="left" plain>
            Pravara
          </Divider>
          <Form.List name="pravara">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Form.Item key={field.key} required={false}>
                    <Space align="baseline" style={{ display: 'flex' }}>
                      <Form.Item
                        {...field}
                        rules={[{ required: true, message: 'Enter pravara' }]}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Input placeholder="Pravara name" />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    </Space>
                  </Form.Item>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add pravara
                </Button>
              </>
            )}
          </Form.List>

          <Divider orientation="left" plain style={{ marginTop: 24 }}>
            थर
          </Divider>
          <Form.List name="surnames">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Card
                    key={field.key}
                    size="small"
                    style={{ marginBottom: 12 }}
                    extra={<MinusCircleOutlined onClick={() => remove(field.name)} />}
                  >
                    <Form.Item
                      {...field}
                      name={[field.name, 'name']}
                      label="Surname"
                      rules={[{ required: true, message: 'Enter surname' }]}
                    >
                      <Input placeholder="e.g. थापा" />
                    </Form.Item>
                    <Form.List name={[field.name, 'categories']}>
                      {(catFields, { add: addCat, remove: removeCat }) => (
                        <>
                          {catFields.map((catField) => (
                            <Form.Item key={catField.key} required={false}>
                              <Space align="baseline" style={{ display: 'flex' }}>
                                <Form.Item
                                  {...catField}
                                  rules={[{ required: true, message: 'Enter category' }]}
                                  style={{ flex: 1, marginBottom: 0 }}
                                >
                                  <Input placeholder="Category e.g. कालिकोटी" />
                                </Form.Item>
                                <MinusCircleOutlined onClick={() => removeCat(catField.name)} />
                              </Space>
                            </Form.Item>
                          ))}
                          <Button
                            type="dashed"
                            size="small"
                            onClick={() => addCat()}
                            icon={<PlusOutlined />}
                          >
                            Add category
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </Card>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add surname
                </Button>
              </>
            )}
          </Form.List>

          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit">
                Create
              </Button>
              <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={
          selectedGotra
            ? drawerMode === 'manage'
              ? `Manage: ${selectedGotra.name}`
              : selectedGotra.name
            : 'Gotra'
        }
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedGotra(null);
        }}
        placement={isMobile ? 'bottom' : 'right'}
        width={isMobile ? '100%' : 560}
        height={isMobile ? 'min(85vh, 720px)' : undefined}
        styles={
          isMobile
            ? {
                body: { paddingBottom: 24 },
                header: { padding: '12px 16px' },
              }
            : undefined
        }
        destroyOnClose
      >
        {selectedGotra && drawerMode === 'view' && renderReadOnlyGotraDetail(selectedGotra)}
        {selectedGotra && drawerMode === 'manage' && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card size="small" title="Validation">
              <Space wrap>
                {renderValidationTag(selectedGotra)}
                {selectedGotra.isValidated ? (
                  <Popconfirm
                    title="Mark this gotra as not validated?"
                    onConfirm={() => handleUnvalidateGotra(selectedGotra.id)}
                    okText="Unvalidate"
                    cancelText="Cancel"
                  >
                    <Button>Unvalidate</Button>
                  </Popconfirm>
                ) : (
                  <Button type="primary" onClick={() => handleValidateGotra(selectedGotra.id)}>
                    Validate
                  </Button>
                )}
              </Space>
            </Card>

            <Card size="small" title="Gotra name">
              <Form
                form={editNameForm}
                layout="inline"
                onFinish={handleUpdateGotraName}
              >
                <Form.Item
                  name="name"
                  rules={[{ required: true, message: 'Enter name' }]}
                  style={{ flex: 1, minWidth: 200 }}
                >
                  <Input />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit">
                    Save
                  </Button>
                </Form.Item>
              </Form>
            </Card>

            <Card
              size="small"
              title="Pravara"
              extra={
                <Text type="secondary" style={{ fontSize: 18 }}>
                  Renaming and pravara validation affect all linked gotras
                </Text>
              }
            >
              {sortPravaraGroupsByOrder(selectedGotra.pravaraGroups || []).map((group, groupIndex, groups) => (
                <div
                  key={group.id}
                  style={{
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <Space style={{ marginBottom: 8 }} wrap>
                    <Text type="secondary">Group {groupIndex + 1}</Text>
                    {renderValidationControls(
                      group.isValidated,
                      () => handleValidatePravaraGroup(group.id),
                      () => handleUnvalidatePravaraGroup(group.id),
                      'Mark this pravara group as not validated?',
                    )}
                    <Button
                      type="text"
                      size="small"
                      icon={<ArrowUpOutlined />}
                      disabled={groupIndex === 0}
                      onClick={() => handleMovePravaraGroup(group.id, -1)}
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<ArrowDownOutlined />}
                      disabled={groupIndex === groups.length - 1}
                      onClick={() => handleMovePravaraGroup(group.id, 1)}
                    />
                    <Popconfirm
                      title="Delete this group? Pravaras will become ungrouped."
                      onConfirm={() => handleDeletePravaraGroup(group.id)}
                    >
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                  <List
                    size="small"
                    dataSource={sortByOrder(group.pravaras)}
                    locale={{ emptyText: 'No pravara in group' }}
                    renderItem={(pravara) => {
                      const fullPravara = selectedGotra.pravara?.find((p) => p.id === pravara.id);
                      if (!fullPravara) return null;
                      return (
                        <List.Item actions={renderPravaraManageActions(fullPravara, { showUngroup: true })}>
                          {renderPravaraManageLabel(fullPravara)}
                        </List.Item>
                      );
                    }}
                  />
                </div>
              ))}

              {getUngroupedPravaras(selectedGotra.pravara || []).length > 0 ? (
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    Ungrouped
                  </Text>
                  <List
                    size="small"
                    dataSource={getUngroupedPravaras(selectedGotra.pravara || [])}
                    renderItem={(pravara) => (
                      <List.Item
                        actions={renderPravaraManageActions(pravara, {
                          showGroupSelect: (selectedGotra.pravaraGroups?.length ?? 0) > 0,
                        })}
                      >
                        {renderPravaraManageLabel(pravara)}
                      </List.Item>
                    )}
                  />
                </div>
              ) : null}

              {!selectedGotra.pravara?.length && !selectedGotra.pravaraGroups?.length ? (
                <Text type="secondary">No pravara</Text>
              ) : null}

              <Space style={{ marginTop: 12 }} wrap>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    createGroupForm.resetFields();
                    setCreateGroupModalOpen(true);
                  }}
                >
                  New group
                </Button>
              </Space>

              <Form form={pravaraForm} layout="inline" onFinish={handleAddPravara} style={{ marginTop: 12 }}>
                <Form.Item
                  name="name"
                  rules={[{ required: true, message: 'Enter pravara' }]}
                  style={{ flex: 1 }}
                >
                  <Input placeholder="New pravara" />
                </Form.Item>
                <Form.Item>
                  <Button type="dashed" htmlType="submit" icon={<PlusOutlined />}>
                    Add
                  </Button>
                </Form.Item>
              </Form>
            </Card>

            <Card size="small" title="थर">
              <Collapse
                accordion
                items={sortSurnamesByOrder(selectedGotra.surnames || []).map((surname, index, sorted) => ({
                  key: String(surname.id),
                  label: renameSurname?.id === surname.id ? (
                    <Input
                      size="small"
                      value={renameSurname.name}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        setRenameSurname({ ...renameSurname, name: e.target.value })
                      }
                      onPressEnter={handleRenameSurname}
                      addonAfter={
                        <Button type="link" size="small" onClick={handleRenameSurname}>
                          Save
                        </Button>
                      }
                    />
                  ) : (
                    <Space>
                      {renderEntityValidationTag(surname.isValidated)}
                      <Text strong>{surname.name}</Text>
                      {surname.categories?.length ? (
                        <Text type="secondary" style={{ fontSize: 18 }}>
                          ({surname.categories.length} categories)
                        </Text>
                      ) : null}
                    </Space>
                  ),
                  extra: (
                    <Space onClick={(e) => e.stopPropagation()}>
                      {renderValidationControls(
                        surname.isValidated,
                        () => handleValidateSurname(surname.id),
                        () => handleUnvalidateSurname(surname.id),
                        'Mark this surname as not validated?',
                      )}
                      <Button
                        type="text"
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={index === 0}
                        onClick={() => handleMoveSurname(surname.id, -1)}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={index === sorted.length - 1}
                        onClick={() => handleMoveSurname(surname.id, 1)}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => setRenameSurname(surname)}
                      />
                      <Popconfirm
                        title="Delete this surname and its categories?"
                        onConfirm={() => handleDeleteSurname(surname.id)}
                      >
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  ),
                  children: (
                    <div>
                      <List
                        size="small"
                        dataSource={surname.categories || []}
                        locale={{ emptyText: 'No categories' }}
                        renderItem={(category) => (
                          <List.Item
                            actions={[
                              renderValidationControls(
                                category.isValidated,
                                () => handleValidateCategory(surname.id, category.id),
                                () => handleUnvalidateCategory(surname.id, category.id),
                                'Mark this category as not validated?',
                              ),
                              <Button
                                key="edit"
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() =>
                                  setRenameCategory({
                                    surnameId: surname.id,
                                    id: category.id,
                                    name: category.name,
                                  })
                                }
                              />,
                              <Popconfirm
                                key="delete"
                                title="Delete category?"
                                onConfirm={() =>
                                  handleDeleteCategory(surname.id, category.id)
                                }
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                />
                              </Popconfirm>,
                            ]}
                          >
                            {renameCategory?.id === category.id &&
                            renameCategory.surnameId === surname.id ? (
                              <Input
                                size="small"
                                value={renameCategory.name}
                                onChange={(e) =>
                                  setRenameCategory({
                                    ...renameCategory,
                                    name: e.target.value,
                                  })
                                }
                                onPressEnter={handleRenameCategory}
                                addonAfter={
                                  <Button
                                    type="link"
                                    size="small"
                                    onClick={handleRenameCategory}
                                  >
                                    Save
                                  </Button>
                                }
                              />
                            ) : (
                              <Space size={8}>
                                {renderEntityValidationTag(category.isValidated)}
                                <span>{category.name}</span>
                              </Space>
                            )}
                          </List.Item>
                        )}
                      />
                      {addCategorySurnameId === surname.id ? (
                        <Form
                          form={categoryForm}
                          layout="inline"
                          onFinish={handleAddCategory}
                          style={{ marginTop: 8 }}
                        >
                          <Form.Item
                            name="name"
                            rules={[{ required: true, message: 'Enter category' }]}
                            style={{ flex: 1 }}
                          >
                            <Input placeholder="Category name" size="small" />
                          </Form.Item>
                          <Form.Item>
                            <Button type="primary" size="small" htmlType="submit">
                              Add
                            </Button>
                          </Form.Item>
                          <Form.Item>
                            <Button size="small" onClick={() => setAddCategorySurnameId(null)}>
                              Cancel
                            </Button>
                          </Form.Item>
                        </Form>
                      ) : (
                        <Button
                          type="dashed"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            categoryForm.resetFields();
                            setAddCategorySurnameId(surname.id);
                          }}
                          style={{ marginTop: 8 }}
                        >
                          Add category
                        </Button>
                      )}
                    </div>
                  ),
                }))}
              />

              <Divider plain>Add surname</Divider>
              <Form form={surnameForm} layout="vertical" onFinish={handleAddSurname}>
                <Form.Item
                  name="name"
                  label="Surname"
                  rules={[{ required: true, message: 'Enter surname' }]}
                >
                  <Input placeholder="e.g. खतिवडा" />
                </Form.Item>
                <Form.List name="categories">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field) => (
                        <Form.Item key={field.key} required={false}>
                          <Space align="baseline" style={{ display: 'flex' }}>
                            <Form.Item
                              {...field}
                              rules={[{ required: true, message: 'Enter category' }]}
                              style={{ flex: 1, marginBottom: 0 }}
                            >
                              <Input placeholder="Category (optional)" />
                            </Form.Item>
                            <MinusCircleOutlined onClick={() => remove(field.name)} />
                          </Space>
                        </Form.Item>
                      ))}
                      <Button
                        type="dashed"
                        size="small"
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                        style={{ marginBottom: 12 }}
                      >
                        Add category
                      </Button>
                    </>
                  )}
                </Form.List>
                <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                  Add surname
                </Button>
              </Form>
            </Card>
          </Space>
        )}
      </Drawer>

      <Modal
        title="New pravara group"
        open={createGroupModalOpen}
        onCancel={() => setCreateGroupModalOpen(false)}
        onOk={() => createGroupForm.submit()}
        confirmLoading={creatingGroup}
        okText="Create"
        destroyOnClose
      >
        <Form
          form={createGroupForm}
          layout="vertical"
          onFinish={(values) => handleCreatePravaraGroup(values.pravaraIds || [])}
        >
          {selectedGotra && getUngroupedPravaras(selectedGotra.pravara || []).length > 0 ? (
            <Form.Item name="pravaraIds" label="Pravaras to include">
              <Select
                mode="multiple"
                placeholder="Select pravaras"
                options={getUngroupedPravaras(selectedGotra.pravara || []).map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
              />
            </Form.Item>
          ) : (
            <Text type="secondary">
              No ungrouped pravaras. An empty group will be created — assign pravaras to it afterward.
            </Text>
          )}
        </Form>
      </Modal>
    </div>
    </ConfigProvider>
  );
};

export default GotraPage;
